import { Alert, Button, Flex, Form, FormInstance } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult } from 'react-query';
import { NouveauDossierConcessionnaireData, RAI } from 'types/Concessionnaire';
import { formatEUR, numberToLocaleString } from 'utils/number';
import ResultatChiffre from 'views/calculer-une-prime/components/ResultatChiffre';

type Props = UseMutationResult<
  {
    id: string;
    volume: number;
    prime: number;
  },
  Error,
  {
    particulier: boolean;
    formData: FormData;
  },
  unknown
> & {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  isLoadingPrime?: boolean;
  primeParGwh?: number;
  primeParVehicule?: number;
  goToPreviousStep(): void;
  volume?: number;
  rai?: RAI;
  showActions?: boolean;
  showError?: boolean;
};

export default function Step3PersonnePhysique({
  form,
  isLoading,
  isLoadingPrime,
  error,
  primeParVehicule,
  goToPreviousStep,
  volume,
  reset,
  showActions = true,
  showError = true,
}: Props) {
  const { t } = useTranslation();
  const volumeMwhc = volume ? numberToLocaleString(volume / 1000, 'MWhc') : '0';
  const primeBeneficiaire = Number(primeParVehicule);

  function handleGoToPreviousStep() {
    reset();
    goToPreviousStep();
  }

  return (
    <Form
      form={form}
      name="step3"
      layout="vertical"
      className="[&_label]:font-bold [&_p]:font-bold [&_div]:text-base"
    >
      <ResultatChiffre
        className="!mb-2"
        label={t('nouveau_formulaire:resultat:categorie_vehicule')}
        value={form.getFieldValue('categorieVehicule')}
        isTotal
      />

      <div className="mb-4">
        <ResultatChiffre
          className="!mb-2"
          label={t('nouveau_formulaire:resultat:volume_de_CEE')}
          value={volumeMwhc}
          isTotal
        />
        <ResultatChiffre
          className="!mb-2"
          label={t('nouveau_formulaire:resultat:prime_beneficiaire')}
          value={primeParVehicule ? formatEUR(primeBeneficiaire) : '0'}
          isTotal
        />
      </div>

      {showActions && (
        <Flex
          justify="space-between"
          className="mt-8 border-t border-color-border pt-4"
        >
          <Button
            type="default"
            onClick={handleGoToPreviousStep}
            size="large"
            shape="round"
            className="px-8"
          >
            {t('global:previous')}
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            shape="round"
            className="px-6"
            loading={isLoading}
            disabled={isLoading || isLoadingPrime}
          >
            {t('global:soumettre_mon_dossier')}
          </Button>
        </Flex>
      )}

      {showError && error && (
        <Flex justify="space-around" className="mb-6">
          <Alert
            message={error.message}
            type="error"
            showIcon
            className="mt-4"
          />
        </Flex>
      )}
    </Form>
  );
}
